import React from 'react';
import PropTypes from 'prop-types';
import generatePassword from 'password-generator';
import copy from 'copy-to-clipboard';

import {
  Button,
  TextField,
  Switch,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import InputAdornment from '@material-ui/core/InputAdornment';
import PhoneIcon from '@material-ui/icons/Phone';
import { DeleteForever } from '@material-ui/icons';

// Role-card icons (kept inside @material-ui/icons to avoid new deps)
import EmojiEventsIcon from '@material-ui/icons/EmojiEvents';
import BusinessIcon from '@material-ui/icons/Business';
import PersonIcon from '@material-ui/icons/Person';
import SecurityIcon from '@material-ui/icons/Security';
import VerifiedUserIcon from '@material-ui/icons/VerifiedUser';
import MenuBookIcon from '@material-ui/icons/MenuBook';
import BuildIcon from '@material-ui/icons/Build';
import TabletIcon from '@material-ui/icons/Tablet';

import { TIME_ZONE, EMPLOYEE_COLORS, PASSWORD_ACTION } from './constants';
import { PERMISSION_SECTIONS, SECTION_BY_KEY } from './permissionSections';
import {
  ROLE_KEYS,
  ROLE_LIST,
  ROLE_PRESETS,
  COMPENSATION,
  buildInitialState,
  buildStateFromAuthorities,
  inferRoleKey,
  isSectionFixed,
  getSectionConfig,
  getAuthorities,
} from './employeeRoles';
import { getCompanyAllowedAuthorities } from '../ManageCompany/wizardConfig';

import './EmployeeWizard.css';

// Roles that are structural/base and must NEVER be stripped by the
// plan-restriction filter — they aren't features you "buy", they're
// scaffolding for login + admin recognition. Also includes the three
// employee-type marker roles, which exist to distinguish Manager /
// Supervisor / Reading Guy in feature gates and are not plan-gated.
const NEVER_FILTERED_ROLES = new Set([
  'ROLE_DASHBOARD',
  'ROLE_USER',
  'ROLE_ADMIN',
  'ROLE_MASTER_ADMIN',
  'ROLE_OWNER',
  'ROLE_TECH_ADMIN',
  'ROLE_COMPANY',
  'ROLE_MANAGER',
  'ROLE_SUPERVISOR',
  'ROLE_READING_GUY',
  // Kiosk marker for the NuVePad tablet login. Not a paid feature, so it
  // must survive the plan-restriction filter and stay on the account.
  'ROLE_TABLET',
  // Free operational permission (delete + force clock-out of employee
  // sessions) — not tied to a plan, so it must never be filtered out.
  'ROLE_MANAGE_EMPLOYEE_SESSIONS',
]);

const ROLE_ICON_BY_KEY = {
  [ROLE_KEYS.OWNER]: EmojiEventsIcon,
  [ROLE_KEYS.TECH_ADMIN]: BuildIcon,
  [ROLE_KEYS.MANAGER]: BusinessIcon,
  [ROLE_KEYS.EMPLOYEE]: PersonIcon,
  [ROLE_KEYS.SUPERVISOR]: SecurityIcon,
  [ROLE_KEYS.READING_GUY]: MenuBookIcon,
  [ROLE_KEYS.SECURITY]: VerifiedUserIcon,
  [ROLE_KEYS.TABLET]: TabletIcon,
};

class EmployeeWizard extends React.Component {
  constructor(props) {
    super(props);

    const isEdit = props.mode === 'edit';
    const u = props.user || {};

    // Determine starting role + toggle state
    const existingAuth = isEdit
      ? (u.authorities || []).map((a) =>
          typeof a === 'string' ? a : a.authority
        )
      : [];
    const initialRoleKey = isEdit
      ? inferRoleKey(existingAuth)
      : ROLE_KEYS.EMPLOYEE;
    const initialToggleState = isEdit
      ? buildStateFromAuthorities(initialRoleKey, existingAuth)
      : buildInitialState(initialRoleKey);

    // Pay type derivation: prefer base pay if any base/threshold/bonus is present
    const hasBasePay =
      Number(u.basePayWeekly) > 0 ||
      Number(u.managerThreshold) > 0 ||
      Number(u.managerBonusPercent) > 0;
    const initialPayType = hasBasePay ? 'basePay' : 'perHour';

    // Derive available companies the same way ManageExpandEdit does
    const availableCompanies = this.computeAvailableCompanies(props);

    this.state = {
      step: 1,
      isMobile: window.matchMedia('(max-width: 800px)').matches,
      isSaving: false,

      // Step 1
      username: u.username || '',
      firstname: u.firstname || '',
      lastname: u.lastname || '',
      phone: u.phone || '',
      timezone: u.timezone || 'America/Chicago',
      password: '',

      // Step 2
      roleKey: initialRoleKey,
      sectionToggles: initialToggleState.sectionToggles,
      subToggles: initialToggleState.subToggles,
      additionalToggles: initialToggleState.additionalToggles,
      payType: initialPayType,

      employeePerHour: u.employeePerHour || '',
      employeeColor: u.employeeColor || '',
      basePayWeekly: u.basePayWeekly || '',
      managerThreshold: u.managerThreshold || '',
      managerBonusPercent: u.managerBonusPercent || '',
      investorPercent: u.investorPercent || '',

      // RFID scanning
      rfid: u.rfid || '',
      isScan: false,
      rfidMessage: '',
      isRfidDeleteOpen: false,
      serverMessage: '',

      // Step 3
      availableCompanies,
      selectedLocations: u.selectedLocations || [],
      locationSearch: '',
      enabled: typeof u.enabled === 'boolean' ? u.enabled : true,
    };

    this.handleRFIDKeyPress = this.handleRFIDKeyPress.bind(this);
  }

  componentDidMount() {
    document.addEventListener('keydown', this.handleRFIDKeyPress);
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleRFIDKeyPress);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.rfid !== this.props.rfid && this.props.rfid) {
      this.setState({ rfid: this.props.rfid });
    }
    if (prevProps.companies !== this.props.companies) {
      this.setState({
        availableCompanies: this.computeAvailableCompanies(this.props),
      });
    }
  }

  getTakenColors() {
    const { users, user, mode } = this.props;
    if (!Array.isArray(users)) return new Set();
    const currentId = mode === 'edit' ? user?.userId : null;
    const locationId = this.props.admin?.selectedLocationId;
    const taken = new Set();
    users.forEach((u) => {
      if (!u || !u.employeeColor) return;
      if (currentId && u.userId === currentId) return;
      if (
        locationId &&
        Array.isArray(u.selectedLocations) &&
        u.selectedLocations.length &&
        !u.selectedLocations.includes(locationId)
      ) {
        return;
      }
      taken.add(u.employeeColor);
    });
    return taken;
  }

  computeAvailableCompanies(props) {
    if (!props.companies) return [];
    if (props.admin?.isOwner) return props.companies;
    const myCompanyId =
      (props.user && props.user.companyId) || props.admin?.company;
    const myCompany = props.companies.find((c) => c.companyId === myCompanyId);
    const linked = myCompany?.linkedCompanies || [];
    return props.companies.filter((c) => linked.includes(c.companyId));
  }

  // Resolve the company whose plan should constrain this employee's
  // permissions: edit mode -> the employee's own company; add mode ->
  // the location the admin is currently managing.
  getRelevantCompany() {
    const { companies, user, admin, mode } = this.props;
    if (!companies || !companies.length) return null;
    const companyId =
      mode === 'edit'
        ? user?.companyId || user?.defaultLocationId
        : admin?.selectedLocationId || admin?.companyId;
    return companies.find((c) => c.companyId === companyId) || null;
  }

  // Set of role strings the company is allowed to grant. Empty set
  // means "no restriction known" — fall back to showing everything.
  getAllowedAuthSet() {
    const company = this.getRelevantCompany();
    if (!company) return null;
    const set = getCompanyAllowedAuthorities(company);
    return set && set.size ? set : null;
  }

  // Multi-location support is gated by ROLE_LINK_ACCOUNTS, which is
  // granted by Pro Elite / Alpha plans or by the link_accounts add-on.
  // Companies without it can't have linked locations to assign.
  companyHasLinkAccounts() {
    const allowed = this.getAllowedAuthSet();
    if (!allowed) return true; // no info → don't restrict
    return allowed.has('ROLE_LINK_ACCOUNTS');
  }

  // Linked-location assignment is only meaningful for senior roles
  // that physically work across multiple locations (Owner, Manager,
  // Reading Guy). Hourly Employees, Supervisors, and Security guards
  // are tied to a single location, so the Locations chip UI is hidden
  // for them and they're auto-pinned to the current company.
  roleCanUseLinkedLocations() {
    return (
      this.state.roleKey === ROLE_KEYS.OWNER ||
      this.state.roleKey === ROLE_KEYS.TECH_ADMIN ||
      this.state.roleKey === ROLE_KEYS.MANAGER ||
      this.state.roleKey === ROLE_KEYS.READING_GUY
    );
  }

  // The Locations chip UI shows only when BOTH the company has linked
  // accounts AND the selected role is one that needs cross-location
  // assignment.
  showLocationsChips() {
    return this.companyHasLinkAccounts() && this.roleCanUseLinkedLocations();
  }

  // Returns a copy of PERMISSION_SECTIONS where each section's subs are
  // filtered down to:
  //   1. Only subs whose role is in the company's allowed set, AND
  //   2. If the active preset narrows the section via `fixedSubs`,
  //      only the subs the preset locks on.
  // Sections with no remaining subs are dropped entirely.
  getVisibleSections() {
    const allowed = this.getAllowedAuthSet();
    const preset = ROLE_PRESETS[this.state.roleKey];
    return PERMISSION_SECTIONS.map((section) => {
      let subs = section.subs;
      if (allowed) {
        subs = subs.filter(
          (sub) => allowed.has(sub.role) || NEVER_FILTERED_ROLES.has(sub.role)
        );
      }
      const lockedSubKeys = preset?.fixedSubs && preset.fixedSubs[section.key];
      if (lockedSubKeys) {
        subs = subs.filter((sub) => lockedSubKeys.includes(sub.key));
      }
      if (!subs.length) return null;
      return { ...section, subs };
    }).filter(Boolean);
  }

  // ---- RFID handling ------------------------------------------------------
  handleRFIDKeyPress(event) {
    if (!this.state.isScan) return;
    event.preventDefault();
    if (event.key === 'Enter') {
      this.setState({ isScan: false, rfidMessage: '' });
      return;
    }
    this.setState((prev) => ({ rfid: prev.rfid + event.key }));
  }

  startScan = () => {
    this.setState({
      isScan: true,
      rfid: '',
      rfidMessage: 'Scanning for tag...',
    });
  };

  registerTag = () => {
    const { rfid } = this.state;
    if (!rfid) {
      this.setState({ rfidMessage: 'Please scan the tag first' });
      return;
    }
    if (!this.props.registerRFID) return;
    this.props.registerRFID(
      { userId: this.props.user?.userId, rfid: rfid.trim() },
      (success, response) => {
        this.setState({
          serverMessage: response,
          rfidMessage: success ? 'Tag registered.' : '',
        });
      }
    );
  };

  confirmDeleteRfid = () => {
    if (this.props.deleteRFID) {
      this.props.deleteRFID({ rfid: this.state.rfid });
    }
    this.setState({ rfid: '', isRfidDeleteOpen: false });
  };

  // ---- Generic handlers ---------------------------------------------------
  handleField = (e) => {
    this.setState({ [e.target.name]: e.target.value });
  };

  selectPayType = (payType) => {
    if (this.state.payType === payType) return;
    if (payType === 'perHour') {
      this.setState({
        payType,
        basePayWeekly: '',
        managerThreshold: '',
        managerBonusPercent: '',
      });
    } else {
      this.setState({
        payType,
        employeePerHour: '',
      });
    }
  };

  createPassword = () => {
    const newPassword = generatePassword(16, false, /[\d\W\w\p]/g).toString();
    this.setState({ password: newPassword });
    copy(newPassword);
  };

  // ---- Step 2: role + section toggles -------------------------------------
  pickRole = (roleKey) => {
    const initial = buildInitialState(roleKey);
    this.setState({
      roleKey,
      sectionToggles: initial.sectionToggles,
      subToggles: initial.subToggles,
      additionalToggles: initial.additionalToggles,
    });
  };

  toggleSection = (sectionKey) => {
    const { roleKey } = this.state;
    if (isSectionFixed(roleKey, sectionKey)) return;
    const preset = ROLE_PRESETS[roleKey];
    const allowedKeys = preset?.fixedSubs && preset.fixedSubs[sectionKey];
    this.setState((prev) => {
      const newOn = !prev.sectionToggles[sectionKey];
      const subToggles = { ...prev.subToggles };
      SECTION_BY_KEY[sectionKey].subs.forEach((sub) => {
        if (allowedKeys && !allowedKeys.includes(sub.key)) {
          subToggles[sub.key] = false;
          return;
        }
        subToggles[sub.key] = newOn;
      });
      return {
        sectionToggles: { ...prev.sectionToggles, [sectionKey]: newOn },
        subToggles,
      };
    });
  };

  toggleSub = (sectionKey, subKey) => {
    const { roleKey } = this.state;
    if (isSectionFixed(roleKey, sectionKey)) return;
    this.setState((prev) => {
      const next = { ...prev.subToggles, [subKey]: !prev.subToggles[subKey] };
      // If any sub in section is on, section is on; otherwise off
      const anyOn = SECTION_BY_KEY[sectionKey].subs.some((s) => next[s.key]);
      return {
        subToggles: next,
        sectionToggles: { ...prev.sectionToggles, [sectionKey]: anyOn },
      };
    });
  };

  toggleAdditional = (key) => {
    this.setState((prev) => ({
      additionalToggles: {
        ...prev.additionalToggles,
        [key]: !prev.additionalToggles[key],
      },
    }));
  };

  // ---- Step 3: locations --------------------------------------------------
  addLocation = (companyId) => {
    this.setState((prev) => {
      if (prev.selectedLocations.includes(companyId)) return null;
      return {
        selectedLocations: [...prev.selectedLocations, companyId],
        locationSearch: '',
      };
    });
  };

  removeLocation = (companyId) => {
    this.setState((prev) => ({
      selectedLocations: prev.selectedLocations.filter(
        (id) => id !== companyId
      ),
    }));
  };

  // ---- Save ---------------------------------------------------------------
  buildPayload = () => {
    const allRoles = this.props.allRoles || [];
    let authorities = getAuthorities({
      roleKey: this.state.roleKey,
      sectionToggles: this.state.sectionToggles,
      subToggles: this.state.subToggles,
      additionalToggles: this.state.additionalToggles,
      allRoles,
    });

    // Hard-cap authorities to what the company's plan actually permits.
    // This catches roles that fixed-section presets (e.g. Owner) try to
    // grant for features the company hasn't paid for.
    const allowed = this.getAllowedAuthSet();
    if (allowed) {
      authorities = authorities.filter(
        (r) => NEVER_FILTERED_ROLES.has(r) || allowed.has(r)
      );
    }

    // Owner always gets ALL linked locations — auto-select everything.
    // When the Locations chip UI is hidden (either the company has no
    // link accounts, or the role doesn't support cross-location work),
    // auto-pin the employee to the current company so the save always
    // has a non-empty selectedLocations list.
    let selectedLocations = this.state.selectedLocations;
    if (this.state.roleKey === ROLE_KEYS.OWNER) {
      selectedLocations = (this.state.availableCompanies || []).map(
        (c) => c.companyId
      );
    } else if (!this.showLocationsChips()) {
      const company = this.getRelevantCompany();
      const fallbackId =
        company?.companyId ||
        this.props.admin?.selectedLocationId ||
        this.props.admin?.companyId;
      selectedLocations = fallbackId ? [fallbackId] : selectedLocations;
    }

    const payload = {
      // identity / Step 1
      username: this.state.username,
      firstname: this.state.firstname,
      lastname: this.state.lastname,
      displayName: `${this.state.firstname} ${this.state.lastname}`.trim(),
      timezone: this.state.timezone,
      phone: this.state.phone,
      password: this.state.password,

      // role / Step 2
      authorities,
      employeePerHour: this.state.employeePerHour,
      employeeColor: this.state.employeeColor,
      basePayWeekly: this.state.basePayWeekly,
      managerThreshold: this.state.managerThreshold,
      managerBonusPercent: this.state.managerBonusPercent,
      investorPercent: this.state.investorPercent,
      rfid: this.state.rfid,

      // locations / Step 3
      selectedLocations,
      enabled: this.state.enabled,
    };

    return payload;
  };

  handleSave = () => {
    // Guard against double-submission: a rapid double-click or pressing
    // Enter while the parent is still dispatching the save action can
    // otherwise fire two /team/add or /team/update calls in a row.
    if (this.state.isSaving) return;
    this.setState({ isSaving: true });
    if (this.state.password) copy(this.state.password);
    this.props.onSave(this.buildPayload());
  };

  // ---- Render helpers -----------------------------------------------------
  renderStepper() {
    const { step } = this.state;
    return (
      <>
        <div className="ew-stepper">
          {[1, 2, 3].map((s) => (
            <div
              key={s}
              className={`ew-stepper__seg ${
                s <= step ? 'ew-stepper__seg--active' : ''
              }`}
            />
          ))}
        </div>
        <div className="ew-stepper__label">
          Step {step} of 3 —{' '}
          {['Info', 'Role & Permissions', 'Locations'][step - 1]}
        </div>
      </>
    );
  }

  renderStep1() {
    return (
      <>
        <h2 className="ew-section-title">Employee Information</h2>
        <div className="ew-form">
          <div className="ew-form-row">
            <TextField
              variant="outlined"
              label="Email Address"
              InputLabelProps={{ shrink: true }}
              name="username"
              value={this.state.username}
              onChange={this.handleField}
              disabled={this.props.mode === 'edit'}
            />
          </div>
          <div className="ew-form-row">
            <TextField
              variant="outlined"
              label="First Name"
              InputLabelProps={{ shrink: true }}
              name="firstname"
              value={this.state.firstname}
              onChange={this.handleField}
            />
            <TextField
              variant="outlined"
              label="Last Name"
              InputLabelProps={{ shrink: true }}
              name="lastname"
              value={this.state.lastname}
              onChange={this.handleField}
            />
          </div>
          <div className="ew-form-row">
            <TextField
              disabled
              variant="outlined"
              label="Display Name"
              InputLabelProps={{ shrink: true }}
              value={`${this.state.firstname} ${this.state.lastname}`.trim()}
            />
            <TextField
              variant="outlined"
              label="Phone"
              InputLabelProps={{ shrink: true }}
              name="phone"
              value={this.state.phone}
              onChange={this.handleField}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneIcon />
                  </InputAdornment>
                ),
              }}
            />
          </div>
          <div className="ew-form-row">
            <Autocomplete
              options={TIME_ZONE}
              getOptionLabel={(t) => t}
              value={this.state.timezone}
              onChange={(_, value) =>
                this.setState({ timezone: value || 'America/Chicago' })
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Time Zone"
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </div>
          <div className="ew-form-row">
            <TextField
              variant="outlined"
              label={
                this.props.mode === 'edit'
                  ? 'New Password (optional)'
                  : 'Password'
              }
              InputLabelProps={{ shrink: true }}
              name="password"
              type="password"
              value={this.state.password}
              onChange={this.handleField}
              autoComplete="new-password"
            />
            <Button className="ew-generate-btn" onClick={this.createPassword}>
              {PASSWORD_ACTION}
            </Button>
          </div>
        </div>
        <div className="ew-actions">
          <button
            className="ew-btn ew-btn--outline"
            onClick={this.props.onClose}
          >
            Cancel
          </button>
          <button
            className="ew-btn ew-btn--primary"
            disabled={!this.canAdvanceFromStep1()}
            onClick={() => this.setState({ step: 2 })}
          >
            Next → Select Role
          </button>
        </div>
      </>
    );
  }

  canAdvanceFromStep1() {
    const { username, firstname, lastname, password } = this.state;
    if (!username || !firstname || !lastname) return false;
    if (this.props.mode === 'add' && !password) return false;
    return true;
  }

  // Returns ROLE_LIST filtered to roles whose `requiredRoles` (if any)
  // are all present in the company's allowed authority set. On the NuVe
  // Data System company only Company Owner and Tech Admin are shown.
  getAvailableRoleList() {
    const allowed = this.getAllowedAuthSet();
    const isOnNuVe = this.props.admin?.selectedLocationName === 'NuVe System';

    // NDS parent company: only Owner and Tech Admin roles
    if (isOnNuVe) {
      return ROLE_LIST.filter(
        (role) =>
          role.key === ROLE_KEYS.OWNER || role.key === ROLE_KEYS.TECH_ADMIN
      );
    }

    return ROLE_LIST.filter((role) => {
      // nuveOnly cards (Tech Admin) hidden when the admin is on a
      // client company — they have their own role presets there.
      if (role.nuveOnly) return false;
      if (!role.requiredRoles || !role.requiredRoles.length) return true;
      if (!allowed) return true;
      return role.requiredRoles.every((r) => allowed.has(r));
    });
  }

  renderRoleCards() {
    const { roleKey } = this.state;
    const availableRoles = this.getAvailableRoleList();
    return (
      <div className="ew-role-grid">
        {availableRoles.map((role) => {
          const Icon = ROLE_ICON_BY_KEY[role.key];
          const active = roleKey === role.key;
          return (
            <div
              key={role.key}
              className={`ew-role-card ${active ? 'ew-role-card--active' : ''}`}
              style={
                active
                  ? { borderColor: role.color, background: `${role.color}10` }
                  : undefined
              }
              onClick={() => this.pickRole(role.key)}
            >
              <div
                className="ew-role-card__icon"
                style={{ background: role.color }}
              >
                <Icon style={{ fontSize: 22 }} />
              </div>
              <div className="ew-role-card__body">
                <p className="ew-role-card__title">{role.label}</p>
                <p className="ew-role-card__desc">{role.description}</p>
              </div>
              <div
                className={`ew-role-card__radio ${
                  active ? 'ew-role-card__radio--checked' : ''
                }`}
              />
            </div>
          );
        })}
      </div>
    );
  }

  renderSection(section, preset) {
    const fixed = preset.fixedSections.includes(section.key);
    const cs = getSectionConfig(this.state.roleKey, section.key);
    const sectionEnabled = !!this.state.sectionToggles[section.key];
    // Count is computed against the (possibly filtered) section.subs
    // we received, not the underlying SECTION_BY_KEY entry, so plan-
    // and preset-restricted views show e.g. "1/1" instead of "1/2".
    const total = section.subs.length;
    const enabled = section.subs.reduce(
      (n, sub) => (this.state.subToggles[sub.key] ? n + 1 : n),
      0
    );

    return (
      <div key={section.key} className="ew-section">
        <div className="ew-section__header">
          <div className="ew-section__num">{section.number}</div>
          <div className="ew-section__label">{section.label}</div>
          {(sectionEnabled || fixed) && (
            <div className="ew-section__count">
              {fixed ? total : enabled}/{total}
            </div>
          )}
          {fixed && <span className="ew-badge ew-badge--locked">LOCKED</span>}
          {!fixed && cs?.badge && (
            <span className="ew-badge ew-badge--default">{cs.badge}</span>
          )}
          {!fixed && (
            <Switch
              checked={sectionEnabled}
              onChange={() => this.toggleSection(section.key)}
              color="primary"
            />
          )}
        </div>
        {(fixed || sectionEnabled) && (
          <div className="ew-section__body">
            {section.subs.map((sub) => {
              const checked = fixed ? true : !!this.state.subToggles[sub.key];
              const cls = `ew-sub__check ${
                fixed
                  ? 'ew-sub__check--locked'
                  : checked
                  ? 'ew-sub__check--checked'
                  : ''
              }`;
              return (
                <div
                  key={sub.key}
                  className={`ew-sub ${fixed ? 'ew-sub--disabled' : ''}`}
                  onClick={() => !fixed && this.toggleSub(section.key, sub.key)}
                >
                  <span className={cls} />
                  <span>{sub.label}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  renderAdditionalAccess(preset) {
    if (!preset.additionalAccess || preset.additionalAccess.length === 0)
      return null;
    const allowed = this.getAllowedAuthSet();
    const visibleAccess = preset.additionalAccess.filter((acc) => {
      if (!allowed) return true;
      // Items without a backing role are always visible (UI-only flags).
      if (!acc.role) return true;
      return allowed.has(acc.role) || NEVER_FILTERED_ROLES.has(acc.role);
    });
    if (visibleAccess.length === 0) return null;
    return (
      <div className="ew-section" style={{ marginTop: 18 }}>
        <div className="ew-section__header">
          <div className="ew-section__label">Additional Access</div>
        </div>
        <div className="ew-section__body" style={{ paddingLeft: 14 }}>
          {visibleAccess.map((acc) => (
            <div
              key={acc.key}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <span style={{ fontSize: 14 }}>{acc.label}</span>
              {acc.fixed ? (
                <span className="ew-badge ew-badge--locked">LOCKED</span>
              ) : (
                <Switch
                  checked={!!this.state.additionalToggles[acc.key]}
                  onChange={() => this.toggleAdditional(acc.key)}
                  color="primary"
                />
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  renderCompensation(preset) {
    if (!preset.compensation) return null;

    const fields = (which) => {
      if (which === 'perHour') {
        return (
          <TextField
            variant="outlined"
            label="Per Hour Rate"
            InputLabelProps={{ shrink: true }}
            type="number"
            name="employeePerHour"
            value={this.state.employeePerHour}
            onChange={this.handleField}
          />
        );
      }
      if (which === 'manager') {
        return (
          <>
            <TextField
              variant="outlined"
              label="Manager Base Pay"
              InputLabelProps={{ shrink: true }}
              type="number"
              name="basePayWeekly"
              value={this.state.basePayWeekly}
              onChange={this.handleField}
            />
            <TextField
              variant="outlined"
              label="Manager Threshold"
              InputLabelProps={{ shrink: true }}
              type="number"
              name="managerThreshold"
              value={this.state.managerThreshold}
              onChange={this.handleField}
            />
            <TextField
              variant="outlined"
              label="Bonus Percentage %"
              InputLabelProps={{ shrink: true }}
              type="number"
              name="managerBonusPercent"
              value={this.state.managerBonusPercent}
              onChange={this.handleField}
            />
          </>
        );
      }
      return null;
    };

    let body;
    if (preset.compensation === COMPENSATION.INVESTOR) {
      body = (
        <TextField
          variant="outlined"
          label="Investor Percentage %"
          InputLabelProps={{ shrink: true }}
          type="number"
          name="investorPercent"
          value={this.state.investorPercent}
          onChange={this.handleField}
        />
      );
    } else if (preset.compensation === COMPENSATION.MANAGER) {
      body = fields('manager');
    } else if (preset.compensation === COMPENSATION.PER_HOUR) {
      body = fields('perHour');
    } else if (preset.compensation === COMPENSATION.PAY_TYPE_TOGGLE) {
      return (
        <div className="ew-comp">
          <div className="ew-comp__label">Compensation</div>
          <div className="ew-paytype">
            <button
              type="button"
              className={`ew-paytype__btn ${
                this.state.payType === 'perHour'
                  ? 'ew-paytype__btn--active'
                  : ''
              }`}
              onClick={() => this.selectPayType('perHour')}
            >
              Per Hour
            </button>
            <button
              type="button"
              className={`ew-paytype__btn ${
                this.state.payType === 'basePay'
                  ? 'ew-paytype__btn--active'
                  : ''
              }`}
              onClick={() => this.selectPayType('basePay')}
            >
              Base Pay
            </button>
          </div>
          <div className="ew-comp__fields">
            {this.state.payType === 'perHour'
              ? fields('perHour')
              : fields('manager')}
          </div>
        </div>
      );
    }

    return (
      <div className="ew-comp">
        <div className="ew-comp__label">Compensation</div>
        <div className="ew-comp__fields">{body}</div>
      </div>
    );
  }

  renderColorAndRfid(preset) {
    if (!preset.showEmployeeColor && !preset.showRfid) return null;
    return (
      <div className="ew-rfid">
        {preset.showEmployeeColor && (
          <div
            className="ew-form-row"
            style={{ marginBottom: 12, display: 'block' }}
          >
            <div style={{ fontSize: 12, color: '#555', marginBottom: 6 }}>
              Employee Color
            </div>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 8,
              }}
            >
              {EMPLOYEE_COLORS.map((option) => {
                const taken = this.getTakenColors().has(option.value);
                const selected = this.state.employeeColor === option.value;
                return (
                  <Tooltip
                    key={option.value}
                    title={taken ? `${option.label} (In use)` : option.label}
                  >
                    <span>
                      <button
                        type="button"
                        disabled={taken}
                        onClick={() =>
                          this.setState({
                            employeeColor: selected ? '' : option.value,
                          })
                        }
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: 6,
                          backgroundColor: option.value,
                          border: selected
                            ? '2px solid #000'
                            : '1px solid rgba(0,0,0,0.2)',
                          cursor: taken ? 'not-allowed' : 'pointer',
                          opacity: taken ? 0.35 : 1,
                          padding: 0,
                          position: 'relative',
                          outline: 'none',
                        }}
                      >
                        {taken && (
                          <span
                            style={{
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              transform: 'translate(-50%, -50%)',
                              color: '#333',
                              fontSize: 14,
                              fontWeight: 'bold',
                              lineHeight: 1,
                            }}
                          >
                            ×
                          </span>
                        )}
                      </button>
                    </span>
                  </Tooltip>
                );
              })}
            </div>
          </div>
        )}
        {preset.showRfid && (
          <>
            <div className="ew-form-row">
              <TextField
                variant="outlined"
                label={preset.rfidOptional ? 'RFID Tag (Optional)' : 'RFID Tag'}
                InputLabelProps={{ shrink: true }}
                value={this.state.rfid ? '******' : ''}
                disabled
                helperText="RFID tag will be auto-populated when scanned."
              />
              {this.state.rfid && this.props.mode === 'edit' && (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Tooltip title="Delete Tag">
                    <IconButton
                      onClick={() => this.setState({ isRfidDeleteOpen: true })}
                    >
                      <DeleteForever fontSize="large" color="error" />
                    </IconButton>
                  </Tooltip>
                </div>
              )}
            </div>
            <div className="ew-form-row" style={{ marginTop: 12 }}>
              <Button
                variant="contained"
                color="primary"
                disabled={!!this.state.rfid && this.props.mode === 'add'}
                onClick={this.startScan}
              >
                Scan Tag
              </Button>
              {this.props.mode === 'edit' && (
                <Button
                  variant="contained"
                  color="primary"
                  disabled={!this.state.rfid}
                  onClick={this.registerTag}
                >
                  Register Tag
                </Button>
              )}
            </div>
            {this.state.rfidMessage && (
              <div className="ew-rfid__msg">{this.state.rfidMessage}</div>
            )}
          </>
        )}
      </div>
    );
  }

  renderStep2() {
    const preset = ROLE_PRESETS[this.state.roleKey];
    const visibleSections = this.getVisibleSections();
    const company = this.getRelevantCompany();
    const planLabel = company?.planName;
    const sectionsRestricted =
      visibleSections.length !== PERMISSION_SECTIONS.length ||
      visibleSections.some(
        (s) =>
          s.subs.length !==
          (PERMISSION_SECTIONS.find((p) => p.key === s.key)?.subs.length || 0)
      );
    // Pro Elite and Alpha already include every plan-level feature —
    // anything still missing on those tiers is a paid add-on, not a
    // plan-upgrade gap. Suppress the "upgrade your plan" notice for
    // them so it doesn't mislead admins into thinking there's a higher
    // plan to switch to.
    const planNameLower = (planLabel || '').toLowerCase();
    const planIncludesAllFeatures =
      planNameLower.includes('alpha') ||
      planNameLower.includes('elite') ||
      planNameLower.includes('pro');
    const showPlanRestrictionNotice =
      sectionsRestricted && planLabel && !planIncludesAllFeatures;
    return (
      <>
        <h2 className="ew-section-title">Select Role & Permissions</h2>
        {this.renderRoleCards()}

        {preset.note && <div className="ew-info-note">{preset.note}</div>}

        {showPlanRestrictionNotice && (
          <div className="ew-info-note">
            Some features are hidden because they aren't included in this
            company's <strong>{planLabel}</strong> plan. Upgrade the plan to
            unlock additional permissions.
          </div>
        )}

        {visibleSections.map((section) => {
          const visible =
            preset.fixedSections.includes(section.key) ||
            preset.configurableSections.some((cs) => cs.key === section.key);
          if (!visible) return null;
          return this.renderSection(section, preset);
        })}

        {this.renderAdditionalAccess(preset)}
        {this.renderCompensation(preset)}
        {this.renderColorAndRfid(preset)}

        <div className="ew-actions">
          <button
            className="ew-btn ew-btn--outline"
            onClick={() => this.setState({ step: 1 })}
          >
            Back
          </button>
          <button
            className="ew-btn ew-btn--primary"
            onClick={() => this.setState({ step: 3 })}
          >
            {this.showLocationsChips()
              ? 'Next → Locations'
              : 'Next → Activation'}
          </button>
        </div>
      </>
    );
  }

  renderStep3() {
    const preset = ROLE_PRESETS[this.state.roleKey];
    const RoleIcon = ROLE_ICON_BY_KEY[this.state.roleKey];
    const showLocations = this.showLocationsChips();
    return (
      <>
        <h2 className="ew-section-title">
          {showLocations ? 'Assign Locations' : 'Activation'}
        </h2>

        <div className="ew-summary">
          <div
            className="ew-summary__icon"
            style={{ background: preset.color }}
          >
            <RoleIcon style={{ fontSize: 20 }} />
          </div>
          <div className="ew-summary__body">
            <p className="ew-summary__role">{preset.label}</p>
            <p className="ew-summary__hint">Permissions configured</p>
          </div>
          <button
            className="ew-summary__change"
            onClick={() => this.setState({ step: 2 })}
          >
            Change
          </button>
        </div>

        {showLocations && (
          <>
            <div className="ew-linked">
              <div className="ew-comp__label">Linked Locations</div>
              {this.state.roleKey === ROLE_KEYS.OWNER ? (
                <div className="ew-info-note">
                  Owners automatically receive access to all linked locations.
                </div>
              ) : (
                <div className="ew-linked__hint">
                  Select which locations this employee can access.
                </div>
              )}

              {this.state.selectedLocations.length > 0 && (
                <div className="ew-linked-chips">
                  {this.state.selectedLocations.map((id) => {
                    const co = (this.state.availableCompanies || []).find(
                      (c) => c.companyId === id
                    );
                    const label = co
                      ? co.companyCode
                        ? `${co.name} (${co.companyCode})`
                        : co.name
                      : id;
                    const isOwner = this.state.roleKey === ROLE_KEYS.OWNER;
                    return (
                      <span key={id} className="ew-linked-chip">
                        <span className="ew-linked-chip__dot" />
                        {label}
                        {!isOwner && (
                          <button
                            type="button"
                            className="ew-linked-chip__x"
                            onClick={() => this.removeLocation(id)}
                            aria-label={`Remove ${label}`}
                          >
                            &times;
                          </button>
                        )}
                      </span>
                    );
                  })}
                </div>
              )}

              {this.state.roleKey !== ROLE_KEYS.OWNER &&
                (() => {
                  const selectedSet = new Set(this.state.selectedLocations);
                  const term = this.state.locationSearch.trim().toLowerCase();
                  const results = term
                    ? (this.state.availableCompanies || [])
                        .filter((c) => !selectedSet.has(c.companyId))
                        .filter(
                          (c) =>
                            (c.name || '').toLowerCase().includes(term) ||
                            String(c.companyCode || '')
                              .toLowerCase()
                              .includes(term)
                        )
                        .slice(0, 8)
                    : [];
                  return (
                    <div className="ew-linked-search">
                      <input
                        className="ew-linked-input"
                        placeholder="Search locations by name or company code..."
                        value={this.state.locationSearch}
                        onChange={(e) =>
                          this.setState({ locationSearch: e.target.value })
                        }
                      />
                      {term && (
                        <div className="ew-linked-results">
                          {results.length === 0 ? (
                            <div className="ew-linked-empty">
                              No matching locations
                            </div>
                          ) : (
                            results.map((c) => (
                              <div
                                key={c.companyId}
                                className="ew-linked-result"
                                onClick={() => this.addLocation(c.companyId)}
                              >
                                <div className="ew-linked-result__name">
                                  {c.name}
                                </div>
                                <div className="ew-linked-result__id">
                                  {c.companyCode || ''}
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  );
                })()}

              {(!this.state.availableCompanies ||
                this.state.availableCompanies.length === 0) && (
                <span style={{ color: '#6B7280', fontSize: 13 }}>
                  No locations available.
                </span>
              )}
            </div>
          </>
        )}

        <div className="ew-activation">
          <div>
            <div style={{ fontWeight: 600 }}>Activation Status</div>
            <div style={{ fontSize: 12, color: '#6B7280' }}>
              When ON, the employee can log in.
            </div>
          </div>
          <Switch
            checked={this.state.enabled}
            onChange={() => this.setState({ enabled: !this.state.enabled })}
            color="primary"
          />
        </div>

        <div className="ew-actions">
          <button
            className="ew-btn ew-btn--outline"
            onClick={() => this.setState({ step: 2 })}
          >
            Back
          </button>
          <button
            className="ew-btn ew-btn--primary"
            onClick={this.handleSave}
            disabled={this.state.isSaving}
          >
            {this.state.isSaving ? 'SAVING...' : 'SAVE'}
          </button>
        </div>
      </>
    );
  }

  render() {
    return (
      <div className="ew-root">
        {this.renderStepper()}
        {this.state.step === 1 && this.renderStep1()}
        {this.state.step === 2 && this.renderStep2()}
        {this.state.step === 3 && this.renderStep3()}

        <Dialog
          open={this.state.isRfidDeleteOpen}
          onClose={() => this.setState({ isRfidDeleteOpen: false })}
        >
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            Are you sure you want to delete {this.state.firstname}'s tag?
          </DialogContent>
          <DialogActions style={{ justifyContent: 'center', marginBottom: 10 }}>
            <Button
              variant="contained"
              onClick={() => this.setState({ isRfidDeleteOpen: false })}
            >
              No
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={this.confirmDeleteRfid}
            >
              Yes
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    );
  }
}

EmployeeWizard.propTypes = {
  mode: PropTypes.oneOf(['add', 'edit']).isRequired,
  user: PropTypes.object,
  admin: PropTypes.object.isRequired,
  users: PropTypes.array,
  companies: PropTypes.array,
  allRoles: PropTypes.array,
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  registerRFID: PropTypes.func,
  deleteRFID: PropTypes.func,
  rfid: PropTypes.string,
};

EmployeeWizard.defaultProps = {
  user: {},
  users: [],
  companies: [],
  allRoles: [],
  rfid: '',
};

export default EmployeeWizard;
